// =============================================================================
// AchievementEditForm: in-place edit surface for an achievement card's own
// fields (LANE ROLE-REFINE — parity matrix gap #7: "the roles you're looking
// for within that role was missing"). Field set mirrors apps/web's
// EditAchievementSheet.tsx: headline + kpiLabel/kpiValue (the card-face
// fields) plus the STAR-L story (situation/task/action/result/learning — the
// brief's "description" maps to this STAR set, the only "description" field
// the schema actually has; apps/web's own edit sheet treats it identically).
// Tag editing, role reassignment, delete, and Reposition (Claude re-telling)
// are apps/web-only surface NOT ported here — this brief's ask is field
// editing, not the full sheet; those remain additive follow-ups on a route
// this app doesn't call yet.
//
// Renders INLINE on the card (AchievementCard's edit-mode branch), not as a
// modal sheet, matching RoleEditForm's same in-place-on-the-canvas choice.
// Every free-text field routes through VoiceTextarea (mic-icon baseline);
// KPI label/value are short fields (rows={1}) for the same reason
// RoleEditForm uses rows={1} for company/title.
//
// NO INVENTION: every field is the user's own typed/dictated edit; nothing
// generated, suggested, or auto-filled. Save only sends the user's real edit.
// =============================================================================

import { useState } from 'react';
import type { AchievementPatch, AchievementRecord } from '../lib/api';
import { VoiceTextarea } from './VoiceTextarea';
import styles from './AchievementEditForm.module.css';

export interface AchievementEditFormProps {
  achievement: AchievementRecord;
  saving: boolean;
  onSave: (patch: AchievementPatch) => Promise<string | null>;
  onCancel: () => void;
}

export function AchievementEditForm({
  achievement,
  saving,
  onSave,
  onCancel,
}: AchievementEditFormProps) {
  const [headline, setHeadline] = useState(achievement.headline);
  const [situation, setSituation] = useState(achievement.situation ?? '');
  const [task, setTask] = useState(achievement.task ?? '');
  const [action, setAction] = useState(achievement.action ?? '');
  const [result, setResult] = useState(achievement.result ?? '');
  const [learning, setLearning] = useState(achievement.learning ?? '');
  const [kpiLabel, setKpiLabel] = useState(achievement.kpiLabel ?? '');
  const [kpiValue, setKpiValue] = useState(achievement.kpiValue ?? '');
  const [error, setError] = useState<string | null>(null);

  const canSave = headline.trim().length > 0;

  async function handleSave() {
    if (!canSave || saving) return;
    setError(null);
    const err = await onSave({
      headline: headline.trim(),
      situation: situation.trim() || null,
      task: task.trim() || null,
      action: action.trim() || null,
      result: result.trim() || null,
      learning: learning.trim() || null,
      kpiLabel: kpiLabel.trim() || null,
      kpiValue: kpiValue.trim() || null,
    });
    if (err) setError(err);
  }

  return (
    <div className={styles.form}>
      <VoiceTextarea label="Headline" value={headline} onChange={setHeadline} rows={2} disabled={saving} />

      <div className={styles.row}>
        <VoiceTextarea
          label="KPI label"
          value={kpiLabel}
          onChange={setKpiLabel}
          rows={1}
          hint="optional"
          disabled={saving}
        />
        <VoiceTextarea
          label="KPI value"
          value={kpiValue}
          onChange={setKpiValue}
          rows={1}
          hint="optional"
          disabled={saving}
        />
      </div>

      <div className={styles.starLabel}>The story (optional)</div>

      <VoiceTextarea
        label="Situation"
        value={situation}
        onChange={setSituation}
        rows={2}
        placeholder="What was the context?"
        disabled={saving}
      />
      <VoiceTextarea
        label="Task"
        value={task}
        onChange={setTask}
        rows={2}
        placeholder="What needed to be done?"
        disabled={saving}
      />
      <VoiceTextarea
        label="Action"
        value={action}
        onChange={setAction}
        rows={2}
        placeholder="What did you do?"
        disabled={saving}
      />
      <VoiceTextarea
        label="Result"
        value={result}
        onChange={setResult}
        rows={2}
        placeholder="Outcome, with a metric where you have one"
        disabled={saving}
      />
      <VoiceTextarea
        label="Learning"
        value={learning}
        onChange={setLearning}
        rows={2}
        hint="optional"
        disabled={saving}
      />

      {error ? (
        <div className={styles.errorRow} role="alert">
          {error}
        </div>
      ) : null}

      <div className={styles.actions}>
        <button type="button" className={styles.cancelButton} onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button
          type="button"
          className={styles.saveButton}
          onClick={handleSave}
          disabled={!canSave || saving}
        >
          {saving ? <span className={styles.spinner} aria-hidden="true" /> : null}
          Save
        </button>
      </div>
    </div>
  );
}
